import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { authFetch, API } from "../utils/api";
import { toast } from "sonner";
import { RefreshCw, X, CheckCircle, Circle } from "lucide-react";

import { ThreadList } from "./InboxPage.ThreadList";
import { ThreadView } from "./InboxPage.ThreadView";

export default function InboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [threadData, setThreadData] = useState(null);
  const [crmContact, setCrmContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replySubject, setReplySubject] = useState("");
  const [composeBizId, setComposeBizId] = useState(null); // set when creating a brand-new thread
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [mobileView, setMobileView] = useState("list");
  const [showStatusDrop, setShowStatusDrop] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const messagesEndRef = useRef(null);
  const statusDropRef = useRef(null);

  // Close status dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (statusDropRef.current && !statusDropRef.current.contains(e.target)) setShowStatusDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchThreads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/inbox`);
      if (res?.ok) setThreads(await res.json());
    } catch { toast.error("Error cargando bandeja"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  // ── Quick-action entry: ?biz=<id>&compose=1 pre-loads stage-aware composer ──
  useEffect(() => {
    const bizId = searchParams.get("biz");
    const compose = searchParams.get("compose");
    if (!bizId || compose !== "1") return;

    (async () => {
      try {
        const r = await authFetch(`${API}/businesses/${bizId}/compose-preview`);
        if (!r?.ok) { toast.error("Error cargando composer"); return; }
        const d = await r.json();

        // Build a thread-like object for the ThreadView (real thread or virtual pending)
        const virtualThread = {
          id: d.thread_id || `virtual-${bizId}`,
          contact_email: d.contact.email,
          contact_name: d.contact.name,
          subject: d.template.subject,
          unread_count: 0,
        };
        setSelected(virtualThread);
        setMobileView("thread");

        // Load real messages if thread exists, else empty
        if (d.thread_id) {
          const tr = await authFetch(`${API}/inbox/${d.thread_id}`);
          if (tr?.ok) setThreadData(await tr.json());
        } else {
          setThreadData({ messages: [], contact: d.contact });
          setComposeBizId(bizId);
        }

        // Pre-fill the reply composer with the stage-aware template
        setReplyText(d.template.body || "");
        setReplySubject(d.template.subject || "");
        setCrmContact({
          id: d.contact.id,
          name: d.contact.name,
          email: d.contact.email,
          status: d.contact.status,
          phone: d.contact.phone,
        });

        if (d.sent_count > 0) {
          toast.success(`Ya le enviaste ${d.sent_count} email(s) — plantilla ajustada a seguimiento`);
        }

        // Strip the query params so a refresh doesn't re-trigger compose
        setSearchParams({}, { replace: true });
      } catch (err) {
        console.error("[Inbox] compose preview failed:", err);
        toast.error("Error cargando composer");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openThread = async (thread) => {
    setSelected(thread);
    setMobileView("thread");
    setLoadingThread(true);
    setReplyText("");
    setReplySubject("");
    setComposeBizId(null);
    setCrmContact(null);
    try {
      const res = await authFetch(`${API}/inbox/${thread.id}`);
      if (res?.ok) {
        const data = await res.json();
        setThreadData(data);
        if (thread.unread_count > 0) {
          await authFetch(`${API}/inbox/${thread.id}/read`, { method: "PUT" });
          setThreads(prev => prev.map(t => t.id === thread.id ? { ...t, unread_count: 0 } : t));
        }
      }
      try {
        const bizRes = await authFetch(`${API}/businesses?search=${encodeURIComponent(thread.contact_email)}&limit=1`);
        if (bizRes?.ok) {
          const rows = await bizRes.json();
          if (Array.isArray(rows) && rows.length > 0) setCrmContact(rows[0]);
        }
      } catch (err) { console.error("[Inbox] Business lookup failed:", err); }
    } catch (err) { toast.error("Error cargando hilo"); console.error("[Inbox] Thread load failed:", err); }
    finally { setLoadingThread(false); }
  };

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
  }, [threadData?.messages]);

  const handleAISuggest = async () => {
    if (!selected || !threadData) return;
    setSuggesting(true);
    try {
      // If in "compose" mode (virtual thread, no existing messages) use the
      // business compose-regenerate endpoint. Otherwise use the in-thread
      // ai-suggest for intent-aware replies.
      if (composeBizId) {
        const res = await authFetch(`${API}/businesses/${composeBizId}/compose-regenerate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            current_subject: replySubject || "",
            current_body: replyText || "",
            hint: "",
          }),
        });
        if (res?.ok) {
          const d = await res.json();
          setReplyText(d.body || replyText);
          setReplySubject(d.subject || replySubject);
          toast.success("Email regenerado con IA");
        } else {
          toast.error("Error al regenerar");
        }
        return;
      }
      const lastReceived = [...(threadData.messages || [])].reverse().find(m => m.direction === "received");
      const settings_res = await authFetch(`${API}/settings`);
      const settings = settings_res?.ok ? await settings_res.json() : {};
      const res = await authFetch(`${API}/inbox/${selected.id}/ai-suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          last_message: lastReceived?.body_text || "Sin mensaje previo",
          contact_name: selected.contact_name || "",
          whatsapp_number: settings.whatsapp_number || "",
        }),
      });
      if (res?.ok) {
        const { suggestion } = await res.json();
        setReplyText(suggestion || "");
        toast.success("Sugerencia de IA lista");
      }
    } catch { toast.error("Error generando sugerencia"); }
    finally { setSuggesting(false); }
  };

  const handleSendReply = async () => {
    if (!selected || !replyText.trim()) return;
    setSending(true);
    try {
      let res;
      if (composeBizId) {
        // Brand-new thread — use quick-email endpoint which creates the thread
        res = await authFetch(`${API}/businesses/${composeBizId}/quick-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subject: replySubject || `Propuesta para ${selected.contact_name || ""}`,
            body: replyText,
          }),
        });
      } else {
        res = await authFetch(`${API}/inbox/${selected.id}/reply`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: replyText, subject: replySubject || undefined }),
        });
      }
      if (res?.ok) {
        toast.success("Respuesta enviada");
        setReplyText("");
        setReplySubject("");
        setComposeBizId(null);
        // After sending, fetch threads and re-open the (now real) thread by email
        await fetchThreads();
        const contactEmail = selected.contact_email;
        const listRes = await authFetch(`${API}/inbox`);
        if (listRes?.ok) {
          const all = await listRes.json();
          const real = all.find(t => (t.contact_email || "").toLowerCase() === (contactEmail || "").toLowerCase());
          if (real) {
            setSelected(real);
            const tr = await authFetch(`${API}/inbox/${real.id}`);
            if (tr?.ok) setThreadData(await tr.json());
          } else if (!composeBizId) {
            const updated = await authFetch(`${API}/inbox/${selected.id}`);
            if (updated?.ok) setThreadData(await updated.json());
          }
        }
      } else { toast.error("Error al enviar la respuesta"); }
    } catch { toast.error("Error al enviar"); }
    finally { setSending(false); }
  };

  const handleUpdateCrmStatus = async (newStatus) => {
    if (!crmContact) return;
    setUpdatingStatus(true);
    setShowStatusDrop(false);
    try {
      const res = await authFetch(`${API}/businesses/${crmContact.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res?.ok) {
        setCrmContact(prev => ({ ...prev, status: newStatus }));
        toast.success("Estado actualizado");
      } else { toast.error("Error al actualizar estado"); }
    } catch { toast.error("Error al actualizar"); }
    finally { setUpdatingStatus(false); }
  };

  const totalUnread = threads.reduce((acc, t) => acc + (t.unread_count || 0), 0);
  const filteredThreads = threads.filter(t => {
    if (filter === "unread" && !t.unread_count) return false;
    if (search) {
      const q = search.toLowerCase();
      return (t.contact_name || "").toLowerCase().includes(q) ||
             (t.subject || "").toLowerCase().includes(q) ||
             (t.contact_email || "").toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="flex overflow-hidden h-[calc(100svh-64px)] md:h-screen" style={{ background: "#0e0e0e", fontFamily: "'Inter', sans-serif" }}>
      {/* Thread list */}
      <div className={`${mobileView === "thread" ? "hidden md:flex" : "flex w-full md:w-auto"} h-full`}>
        <ThreadList
          threads={filteredThreads}
          selected={selected}
          filter={filter}
          loading={loading}
          onSelect={openThread}
          search={search}
          onSearch={setSearch}
          totalUnread={totalUnread}
          onThreadsChanged={() => {
            fetchThreads();
            // If the deleted thread was open, clear view
            if (selected && !threads.some(t => t.id === selected.id)) {
              setSelected(null);
              setThreadData(null);
            }
          }}
        />
      </div>

      {/* Thread view + contact sidebar */}
      <div className={`${mobileView === "list" ? "hidden md:flex" : "flex"} flex-1 overflow-hidden h-full`}>
        <ThreadView
          selected={selected}
          threadData={threadData}
          crmContact={crmContact}
          loadingThread={loadingThread}
          messagesEndRef={messagesEndRef}
          replyText={replyText}
          setReplyText={setReplyText}
          replySubject={replySubject}
          setReplySubject={setReplySubject}
          onSendReply={handleSendReply}
          sending={sending}
          onAISuggest={handleAISuggest}
          suggesting={suggesting}
          showStatusDrop={showStatusDrop}
          statusDropRef={statusDropRef}
          onToggleStatusDrop={() => setShowStatusDrop(s => !s)}
          onUpdateCrmStatus={handleUpdateCrmStatus}
          updatingStatus={updatingStatus}
          onBack={() => setMobileView("list")}
        />
      </div>
    </div>
  );
}
